import React, { useState } from "react";
import { IconAdd, IconRemove } from "../icons";

function toFiniteNumber(value, fallback) {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function toLimit(value, fallback) {
  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function clampValue(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function normalizeStep(step) {
  const numericStep = Number(step);

  return Number.isFinite(numericStep) && numericStep > 0 ? numericStep : 1;
}

function normalizePrecision(value) {
  const normalizedValue = Number.parseFloat(value.toFixed(12));

  return Object.is(normalizedValue, -0) ? 0 : normalizedValue;
}

export default function AppCounter({
  size = "md",
  invalid = false,
  className = "",
  style,
  value,
  defaultValue = 0,
  onChange,
  onValueChange,
  min = 0,
  max,
  step = 1,
  disabled = false,
  readOnly = false,
  name,
  formatValue,
  decrementAriaLabel = "Wert verringern",
  incrementAriaLabel = "Wert erhöhen",
  role = "group",
  ...props
}) {
  const lowerLimit = toLimit(min, Number.NEGATIVE_INFINITY);
  const rawUpperLimit = toLimit(max, Number.POSITIVE_INFINITY);
  const upperLimit = rawUpperLimit < lowerLimit ? lowerLimit : rawUpperLimit;
  const stepValue = normalizeStep(step);
  const fallbackValue = Number.isFinite(lowerLimit) ? lowerLimit : 0;
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(() =>
    clampValue(toFiniteNumber(defaultValue, fallbackValue), lowerLimit, upperLimit)
  );
  const currentValue = clampValue(
    toFiniteNumber(isControlled ? value : internalValue, fallbackValue),
    lowerLimit,
    upperLimit
  );
  const decrementDisabled = disabled || readOnly || currentValue <= lowerLimit;
  const incrementDisabled = disabled || readOnly || currentValue >= upperLimit;
  const displayValue = typeof formatValue === "function" ? formatValue(currentValue) : currentValue;

  const classes = [
    "ds-counter",
    size === "sm" ? "ds-counter--sm" : "",
    size === "md" ? "ds-counter--md" : "",
    invalid ? "ds-control--invalid" : "",
    disabled ? "ds-counter--disabled" : "",
    readOnly ? "ds-counter--readonly" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  function createChangeEvent(nextValue, action) {
    const target = {
      value: nextValue,
      name,
      action,
    };

    return {
      type: "change",
      target,
      currentTarget: target,
    };
  }

  function commitValue(nextValue, action) {
    if (disabled || readOnly) {
      return;
    }

    const normalizedValue = normalizePrecision(clampValue(nextValue, lowerLimit, upperLimit));

    if (normalizedValue === currentValue) {
      return;
    }

    const changeEvent = createChangeEvent(normalizedValue, action);

    if (!isControlled) {
      setInternalValue(normalizedValue);
    }

    onChange?.(changeEvent);
    onValueChange?.(normalizedValue, changeEvent);
  }

  return (
    <div
      className={classes}
      style={style}
      role={role}
      data-readonly={readOnly ? "true" : undefined}
      aria-invalid={invalid ? "true" : undefined}
      {...props}
    >
      <button
        type="button"
        className="ds-counter__button ds-counter__button--decrement"
        disabled={decrementDisabled}
        aria-label={decrementAriaLabel}
        onClick={() => commitValue(currentValue - stepValue, "decrement")}
      >
        <IconRemove sx={{ fontSize: "var(--ds-counter-icon-size)" }} aria-hidden="true" />
      </button>
      <span className="ds-counter__value" aria-live="polite">
        {displayValue}
      </span>
      <button
        type="button"
        className="ds-counter__button ds-counter__button--increment"
        disabled={incrementDisabled}
        aria-label={incrementAriaLabel}
        onClick={() => commitValue(currentValue + stepValue, "increment")}
      >
        <IconAdd sx={{ fontSize: "var(--ds-counter-icon-size)" }} aria-hidden="true" />
      </button>
    </div>
  );
}
